/* eslint-disable react-refresh/only-export-components -- provider, hook,
   and error type are colocated by convention (matches views/ViewContext). */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useNavigate } from 'react-router'

import { env } from '../lib/env'
import { Sentry } from '../lib/sentry'
import { isAdminRole } from './nav'

const STORAGE_KEY = 'medairbnb.admin.token'

export type AdminUser = {
  id: string
  role: string
}

type AuthContextValue = {
  user: AdminUser | null
  token: string | null
  login: (email: string, password: string) => Promise<void>
  /** Sign out. `reason` is shown on the login screen (see `LoginPage`). */
  logout: (reason?: SignOutReason) => void
}

/** Why the admin signed someone out, when it was not their choice. */
export type SignOutReason = 'role_not_permitted'

/** Thrown by `login()` carrying the server's machine-readable `error.code`
 * (or `network_error`) so the login screen can pick the right message. */
export class AuthError extends Error {
  code: string
  constructor(code: string, message: string) {
    super(message)
    this.name = 'AuthError'
    this.code = code
  }
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

type JwtClaims = { sub: string; role: string; exp: number }

/** Decode a JWT payload WITHOUT verifying it (the server already signed it;
 * we only read claims to drive the UI). Returns null on anything malformed. */
function decodeJwt(token: string): JwtClaims | null {
  try {
    const payload = token.split('.')[1]
    if (!payload) return null
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
    const claims = JSON.parse(atob(padded)) as Record<string, unknown>
    if (
      typeof claims.sub === 'string' &&
      typeof claims.role === 'string' &&
      typeof claims.exp === 'number'
    ) {
      return { sub: claims.sub, role: claims.role, exp: claims.exp }
    }
  } catch {
    // malformed token segment / JSON — treat as no token
  }
  return null
}

function safeGet(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

function safeSet(token: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, token)
  } catch {
    // in-memory state still updates; storage is best-effort
  }
}

function safeRemove(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}

function userFromToken(token: string): AdminUser | null {
  const claims = decodeJwt(token)
  if (!claims || claims.exp * 1000 <= Date.now()) return null
  return { id: claims.sub, role: claims.role }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const [token, setToken] = useState<string | null>(safeGet)
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  // `user` is derived from the token; null when absent or expired.
  const user = useMemo(() => (token ? userFromToken(token) : null), [token])

  const logout = useCallback(
    (reason?: SignOutReason) => {
      if (timer.current) clearTimeout(timer.current)
      safeRemove()
      Sentry.setUser(null)
      setToken(null)
      navigate('/admin/login', {
        replace: true,
        state: reason ? { signedOut: reason } : undefined,
      })
    },
    [navigate],
  )

  // Auto-logout at token expiry. Scheduled via setTimeout so logout() (which
  // sets state) never runs synchronously in the effect body. An already-
  // expired or malformed token schedules at 0ms, so it logs out on the next
  // tick — while `user` is already null this render (derived from the token).
  useEffect(() => {
    if (!token) return
    const claims = decodeJwt(token)
    const msLeft = claims ? claims.exp * 1000 - Date.now() : 0
    timer.current = setTimeout(() => logout(), Math.max(0, msLeft))
    return () => {
      if (timer.current) clearTimeout(timer.current)
    }
  }, [token, logout])

  const login = useCallback(async (email: string, password: string) => {
    let resp: Response
    try {
      resp = await fetch(`${env.apiBaseUrl}/api/v1/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      })
    } catch {
      throw new AuthError('network_error', 'Could not reach the server')
    }

    if (!resp.ok) {
      let code = 'invalid_credentials'
      try {
        const body = (await resp.json()) as { error?: { code?: string } }
        if (body?.error?.code) code = body.error.code
      } catch {
        // non-JSON error body — keep the default code
      }
      throw new AuthError(code, 'Login failed')
    }

    const body = (await resp.json()) as { access_token?: string }
    const newToken = body.access_token
    const claims = newToken ? decodeJwt(newToken) : null
    if (!newToken || !claims) {
      throw new AuthError('invalid_token', 'Server returned a malformed token')
    }
    // A valid account whose role the admin does not admit (a patient, say).
    // Refused here, before the token is stored, so it never reaches a page.
    if (!isAdminRole(claims.role)) {
      throw new AuthError(
        'role_not_permitted',
        'This account cannot use the admin',
      )
    }
    safeSet(newToken)
    Sentry.setUser({ id: claims.sub })
    setToken(newToken)
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({ user, token, login, logout }),
    [user, token, login, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>')
  }
  return ctx
}
